import images from './brandProductImages.json'
import routes from './brandRoutes.json'
import milieus from './brandMilieuImages.json'

type Size = 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
export type BrandScene = {
  brandId: number
  menuId?: number
  productId: number
  image: { src: string } & Partial<Record<Size, string>>
  alt: string
  logo?: string
  scene: boolean
  width: number
  height: number
  productHref: string
}
export const brandScenes: Record<string, BrandScene | null> = images
export const brandRoutes: Record<string, { id: number; title: string; path: string; logo?: string }> = routes
// These categories already filter to one named brand, including their direct
// /m/ URLs. General categories (e.g. Vasen) must remain brand-independent.
export const brandMenuBrands: Record<number, number> = {
  33001333: 39, 33001334: 39, 33001340: 39, 33001341: 39, 33001342: 39, 33001343: 39,
  33001344: 36, 33001345: 36, 33001346: 36, 33001347: 36, 33001348: 36,
  33001349: 183, 33001350: 183, 33001351: 183, 33001352: 183, 33001353: 183,
  33001354: 209, 33001355: 209, 33001356: 209, 33001357: 209,
  33001358: 200, 33001359: 200, 33001360: 200, 33001361: 200,
  33001362: 166, 33001363: 166, 33001364: 166, 33001365: 166, 33001366: 166, 33001367: 166, 33001368: 166,
  33001369: 210, 33001370: 210, 33001371: 210,
  33001372: 35, 33001373: 35, 33001374: 34, 33001375: 34,
  33001376: 156, 33001377: 156, 33001378: 211, 33001379: 211, 33001380: 211,
  33001381: 190, 33001382: 190,
}
export type BrandMilieu = {
  filename: string
  alt: string
  width: number
  height: number
  brandId: number
  menuId?: number
  productIds: number[]
  logoEmbedded: boolean
}
export const brandMilieus: Record<string, BrandMilieu> = milieus
const widths: Record<Size, number> = { sm: 360, md: 640, lg: 768, xl: 960, xxl: 1920 }
const responsiveWidths = [360, 480, 640, 768, 960, 1024, 1200, 1280, 1600, 1920]

export const brandSceneKey = (brandId: number, menuId?: number) =>
  menuId ? `brand-menu-${brandId}-${menuId}` : `brand-${brandId}`

const cachedMilieuUrl = (image: BrandMilieu, width: number) =>
  `/image/landingpages/202/${image.filename.replace(/\.webp$/, `-w${width}.webp`)}`

export const brandSceneImageUrl = (scene: BrandScene, size: Size = 'xl') => {
  const image = brandMilieus[brandSceneKey(scene.brandId, scene.menuId)]
  return image ? cachedMilieuUrl(image, widths[size]) : undefined
}

// Original catalog references remain separate from the reviewed compositions.
// Every rendered URL points straight to a prebuilt, versioned WebP cache entry.
const presentations = new Map<string, {
  srcSet: string; brandId: number; productId: number; width: number; height: number; logoEmbedded: boolean
}>()
for (const [key, image] of Object.entries(brandMilieus)) {
  const scene = brandScenes[key]
  if (!scene) continue
  const availableWidths = responsiveWidths.filter((width, index, all) =>
    index === 0 || Math.min(width, image.width) !== Math.min(all[index - 1], image.width))
  const srcSet = availableWidths
    .map(width => `${cachedMilieuUrl(image, width)} ${Math.min(width, image.width)}w`).join(', ')
  const presentation = {
    srcSet, brandId: scene.brandId, productId: scene.productId,
    width: image.width, height: image.height, logoEmbedded: image.logoEmbedded,
  }
  for (const width of responsiveWidths) presentations.set(cachedMilieuUrl(image, width), presentation)
}

export const getBrandImagePresentation = (src: string) => {
  let path = src
  if (/^https?:\/\//.test(src)) {
    try {
      const url = new URL(src)
      if (!['liebeszuhause.de', 'www.liebeszuhause.de'].includes(url.hostname)) return undefined
      path = url.pathname
    } catch { return undefined }
  }
  return presentations.get(path)
}
